/**
 * Dev mode — in-memory store of seed data + overrides for the Supabase calls.
 * When `useApp.dev === true`, the surface components use this module instead of `db.ts`.
 * No network calls. Pure local state.
 */
import { SEED_MEMBERS, SEED_TASKS, SEED_MESSAGES, SEED_RESOURCES, SEED_GAMIFICATION } from './seed'
import type { DbTask, DbMessage, DbResource, DbMember } from './db'

let tasks = [...SEED_TASKS]
let messages = [...SEED_MESSAGES]
const resources = [...SEED_RESOURCES]
const members = [...SEED_MEMBERS]
const gamification: Record<string, typeof SEED_GAMIFICATION[string]> = JSON.parse(JSON.stringify(SEED_GAMIFICATION))

export const devDb = {
  members: {
    list: async () => ({ data: members, error: null }),
    update: async (id: string, updates: Partial<DbMember>) => {
      const idx = members.findIndex((m) => m.id === id)
      if (idx >= 0) members[idx] = { ...members[idx], ...updates }
      return { data: null, error: null }
    },
  },
  tasks: {
    list: async () => {
      const filtered = tasks.filter((t) => t.status !== 'archived')
      return { data: filtered, error: null }
    },
    create: async (task: Partial<DbTask>) => {
      const id = `dev-task-${tasks.length + 1}`
      const newTask: DbTask = {
        id,
        title: task.title ?? 'Untitled',
        type: task.type ?? 'chore',
        priority: task.priority ?? 'med',
        ep: task.ep ?? 10,
        due: task.due ?? null,
        assignees: task.assignees ?? [],
        status: task.status ?? 'todo',
        position: task.position ?? tasks.length,
        created_by: task.created_by ?? 'dev-mom',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        done_at: null,
        archived_at: null,
        recurrence: task.recurrence ?? 'none',
        recurrence_parent_id: task.recurrence_parent_id ?? null,
      }
      tasks = [...tasks, newTask]
      return { data: newTask, error: null }
    },
    updateStatus: async (id: string, status: DbTask['status']) => {
      const idx = tasks.findIndex((t) => t.id === id)
      if (idx >= 0) {
        const updated = { ...tasks[idx], status, updated_at: new Date().toISOString() }
        if (status === 'done') updated.done_at = new Date().toISOString()
        tasks[idx] = updated
      }
      return { data: null, error: null }
    },
    move: async (id: string, status: DbTask['status'], position: number) => {
      const idx = tasks.findIndex((t) => t.id === id)
      if (idx >= 0) {
        const prev = tasks[idx]
        const updated = { ...prev, status, position, updated_at: new Date().toISOString() }
        if (status === 'done' && !prev.done_at) updated.done_at = new Date().toISOString()
        if (status !== 'done') updated.done_at = null
        tasks[idx] = updated

        // EP credit: when status transitions to done, add pending_ep to each assignee
        const goingToDone = status === 'done' && prev.status !== 'done'
        const leavingDone = status !== 'done' && prev.status === 'done'
        if (goingToDone || leavingDone) {
          const delta = (goingToDone ? 1 : -1) * prev.ep
          for (const memberId of prev.assignees) {
            const g = gamification[memberId]
            if (g) {
              gamification[memberId] = {
                ...g,
                pending_ep: Math.max(0, g.pending_ep + delta),
                lifetime_ep: Math.max(0, g.lifetime_ep + delta),
              }
            }
          }
        }
      }
      return { data: null, error: null }
    },
    /** Spawn the next occurrence of a recurring task when the current is approved (done). */
    spawnNext: async (parentId: string) => {
      const parent = tasks.find((t) => t.id === parentId)
      if (!parent) return { data: null, error: 'parent not found' }
      if (parent.recurrence === 'none') return { data: null, error: 'not recurring' }
      // Import the helper dynamically to avoid circular import at module load
      const { nextRecurrenceDue } = await import('./db')
      const nextDue = nextRecurrenceDue(parent.recurrence, parent.due)
      if (!nextDue) return { data: null, error: 'no next due' }
      const now = new Date().toISOString()
      const newTask: DbTask = {
        id: `dev-task-${tasks.length + 1}-${Date.now().toString(36)}`,
        title: parent.title,
        type: parent.type,
        priority: parent.priority,
        ep: parent.ep,
        due: nextDue,
        assignees: [...parent.assignees],
        status: 'todo',
        position: 0,
        created_by: parent.created_by,
        created_at: now,
        updated_at: now,
        done_at: null,
        archived_at: null,
        recurrence: parent.recurrence,
        recurrence_parent_id: parent.recurrence_parent_id ?? parent.id,
      }
      tasks = [...tasks, newTask]
      return { data: newTask, error: null }
    },
  },
  gamification: {
    get: async (memberId: string) => {
      return { data: gamification[memberId] ?? { ep: 0, level: 1, streak_days: 0, last_streak_date: null, achievements: [] }, error: null }
    },
  },
  messages: {
    list: async () => ({ data: messages, error: null }),
    send: async (channel: DbMessage['channel'], body: string, sender: string, dmWith: string | null = null) => {
      const newMsg: DbMessage = {
        id: `dev-msg-${messages.length + 1}`,
        channel,
        dm_with: dmWith,
        sender,
        body,
        created_at: new Date().toISOString(),
      }
      messages = [...messages, newMsg]
      return { data: newMsg, error: null }
    },
  },
  resources: {
    list: async () => ({ data: resources, error: null }),
  },
  history: {
    list: async () => ({ data: tasks.filter((t) => t.status === 'done'), error: null }),
  },
}
